<template>
  <div class="prod-stack">
    <form class="move-card prod-panel space-y-4" @submit.prevent="add">
      <ProductionSectionHead title="Comments" subtitle="Notes for the whole production — not tied to a scene." />
      <div class="move-field !mb-0">
        <label for="comment-body">Write a comment</label>
        <textarea id="comment-body" v-model="content" required rows="3" class="move-textarea" placeholder="Need a second camera for B-roll…" />
      </div>
      <button type="submit" class="move-btn-primary">Post comment</button>
    </form>

    <div v-if="loading" class="flex justify-center py-10">
      <div class="move-spinner" aria-label="Loading comments" />
    </div>
    <div v-else-if="!comments.length" class="move-card move-empty-state py-12">
      <span class="material-icons">chat</span>
      <p class="font-semibold move-text">No comments yet</p>
      <p class="text-sm move-text-faint mt-1">Leave a note for the team.</p>
    </div>
    <ul v-else class="space-y-3">
      <li v-for="comment in comments" :key="comment.id" class="move-card prod-comment">
        <span class="prod-person-avatar">{{ initials(`${comment.creator?.name || ''} ${comment.creator?.surname || ''}`) }}</span>
        <div class="min-w-0 flex-1">
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-sm font-semibold move-text">{{ comment.creator?.name }} {{ comment.creator?.surname }}</p>
              <p class="text-xs move-text-faint">{{ formatDateTime(comment.created_at) }}</p>
            </div>
            <button type="button" class="move-icon-btn move-icon-btn--danger" aria-label="Delete comment" @click="askRemove(comment)">
              <span class="material-icons">close</span>
            </button>
          </div>
          <p class="mt-2 whitespace-pre-wrap text-sm move-text">{{ comment.content }}</p>
        </div>
      </li>
    </ul>

    <MoveConfirmDialog
      v-model="showRemoveModal"
      title="Delete comment"
      subtitle="This action cannot be undone."
      :detail-title="commentPreview"
      detail-tone="danger"
      confirm-label="Delete comment"
      confirm-icon="delete"
      busy-label="Deleting…"
      :busy="removing"
      :error="removeError"
      @confirm="confirmRemove"
      @cancel="closeRemove"
    />
  </div>
</template>

<script setup>
import { computed, inject, onMounted, ref } from 'vue'
import ProductionSectionHead from '../../components/production/ProductionSectionHead.vue'
import MoveConfirmDialog from '../../components/MoveConfirmDialog.vue'
import { productionApi } from '../../services/productionApi'
import { formatDateTime, initials } from '../../utils/productionDates'

const workspace = inject('productionWorkspace')
const comments = ref([])
const loading = ref(true)
const content = ref('')
const showRemoveModal = ref(false)
const commentToRemove = ref(null)
const removing = ref(false)
const removeError = ref('')
const commentPreview = computed(() => {
  const text = String(commentToRemove.value?.content || '').trim()
  if (!text) return 'This comment will be deleted.'
  return text.length > 160 ? `${text.slice(0, 160).trim()}…` : text
})

async function load () {
  loading.value = true
  const body = await productionApi.listComments(workspace.productionId.value)
  comments.value = body.data || []
  loading.value = false
}

async function add () {
  await productionApi.addComment(workspace.productionId.value, content.value)
  content.value = ''
  await load()
}

function askRemove (comment) {
  commentToRemove.value = comment
  removeError.value = ''
  showRemoveModal.value = true
}

function closeRemove () {
  if (removing.value) return
  showRemoveModal.value = false
  commentToRemove.value = null
  removeError.value = ''
}

async function confirmRemove () {
  const comment = commentToRemove.value
  if (!comment || removing.value) return
  removing.value = true
  removeError.value = ''
  try {
    await productionApi.deleteComment(comment.id)
    showRemoveModal.value = false
    commentToRemove.value = null
    await load()
  } catch (e) {
    removeError.value = e.response?.data?.message || 'Could not delete this comment.'
  } finally {
    removing.value = false
  }
}

onMounted(load)
</script>
